import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Users, Edit2, Trash2, User, Globe, AlertCircle, Save, X, Loader, Plus, Mail, Search, RefreshCw, Clock, CheckCircle2, ChevronDown } from 'lucide-react';
import config from '../config';
import BulleSurvol from './BulleSurvol';
import '../AccessStyles.css';
import '../styles/compteur.css';
import CreateClientModal from './kanban/modals/CreateClientModal';
/*
 * ⚠️ Les accès d'une carte passent par le module partagé, jamais par un `fetch` écrit ici.
 *
 * Trois écrans font le même geste — celui-ci, la modale du Gestionnaire, et la fiche d'un accès
 * client —, et les deux premiers avaient déjà divergé sur la lecture de la réponse. L'URL, la
 * forme et la sémantique d'`isActive` vivent désormais au même endroit.
 */
import {
  lireAccesDUneCarte, rattacherEmail, detacherEmail, changerActivite, relancerInvitation
} from '../api/accesCartes';

const ClientsView = ({ token, onClientChange, clientFiltre = null }) => {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [clientAccess, setClientAccess] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tri, setTri] = useState({ cle: 'nom', sens: 'asc' });

  /**
   * Les sites du parc, groupés par client, pour la bulle du survol.
   *
   * `/api/clients` ne rend que le **décompte** (`_count.sites`) : savoir qu'un client a trois
   * sites sans pouvoir les nommer oblige à ouvrir la modale pour une question à laquelle un
   * survol répond. Un seul appel à `/api/sites`, qui porte déjà le `clientId` de chacun, suffit
   * — inutile d'interroger client par client.
   */
  const [sitesParClient, setSitesParClient] = useState(null);
  const [survol, setSurvol] = useState(null);
  const [resending, setResending] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  useEffect(() => {
    fetchClients();
    fetchAvailableSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/client-management/clients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setClients(data);
      }
    } catch (error) {
      console.error('Erreur récupération clients:', error);
      setError('Erreur lors de la récupération des clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSites = async () => {
    try {
      const response = await fetch(`${config.API_URL}/api/client-management/available-sites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (!data.error) {
        setAvailableSites(data);
      }
    } catch (error) {
      console.error('Erreur récupération sites disponibles:', error);
    }
  };

  const fetchClientSites = async (clientId) => {
    try {
      const response = await fetch(`${config.API_URL}/api/client-management/clients/${clientId}/sites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (!data.error) {
        setSites(data);
      }
    } catch (error) {
      console.error('Erreur récupération sites client:', error);
    }
  };


  const handleEditClient = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${config.API_URL}/api/client-management/clients/${selectedClient.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setClients(clients.map(c => c.id === selectedClient.id ? data : c));
        setShowEditModal(false);
        setSelectedClient(null);
        setFormData({ name: '', email: '' });
        setError('');
        // Notifier le parent du changement
        if (onClientChange) onClientChange();
      }
    } catch (error) {
      console.error('Erreur modification client:', error);
      setError('Erreur lors de la modification du client');
    }
  };

  const handleDeleteClient = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    const emailCount = client?.clientEmails?.length || 0;
    const emailsList = client?.clientEmails?.map(e => e.email).join('\n• ') || '';
    
    let confirmMessage = `⚠️ SUPPRESSION DÉFINITIVE ⚠️\n\nÊtes-vous sûr de vouloir supprimer le client "${client?.name}" ?\n\n`;
    
    if (emailCount > 0) {
      confirmMessage += `🔥 ATTENTION : Cette action supprimera également :\n`;
      confirmMessage += `• ${emailCount} adresse(s) email d'accès à ce client :\n• ${emailsList}\n\n`;
      confirmMessage += `📧 Les comptes utilisateurs seront supprimés UNIQUEMENT s'ils ne donnent accès à aucun autre client.\n\n`;
      confirmMessage += `Cette action est IRRÉVERSIBLE !`;
    } else {
      confirmMessage += `Cette action est IRRÉVERSIBLE !`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}/api/client-management/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setClients(clients.filter(c => c.id !== clientId));
        
        // Afficher un message de succès avec les détails des utilisateurs supprimés et préservés
        let successMessage = `✅ Client supprimé avec succès !\n\n`;
        
        if (data.deletedUsers > 0) {
          const deletedEmailsList = data.deletedUserEmails.join(', ');
          successMessage += `🗑️ ${data.deletedUsers} compte(s) utilisateur supprimé(s) :\n• ${deletedEmailsList}\n\n`;
        }
        
        if (data.keptUsers > 0) {
          const keptEmailsList = data.keptUserEmails.join(', ');
          successMessage += `🔒 ${data.keptUsers} compte(s) utilisateur préservé(s) (ils donnent accès à d'autres clients) :\n• ${keptEmailsList}\n\n`;
        }
        
        if (data.deletedUsers === 0 && data.keptUsers === 0) {
          successMessage += `ℹ️ Aucun compte utilisateur CLIENT associé n'a été trouvé.`;
        }
        
        alert(successMessage);
        
        setError('');
        // Notifier le parent du changement
        if (onClientChange) onClientChange();
      }
    } catch (error) {
      console.error('Erreur suppression client:', error);
      setError('Erreur lors de la suppression du client');
    }
  };

  const handleAssignSite = async (siteId, assign) => {
    try {
      const response = await fetch(`${config.API_URL}/api/client-management/clients/${selectedClient.id}/sites/${siteId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ associate: assign })
      });

      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        // Rafraîchir les données
        fetchClientSites(selectedClient.id);
        fetchAvailableSites();
        setError('');
      }
    } catch (error) {
      console.error('Erreur association site:', error);
      setError('Erreur lors de l\'association du site');
    }
  };

  const fetchClientAccess = async (clientId) => {
    try {
      setClientAccess(await lireAccesDUneCarte(token, clientId));
    } catch (error) {
      console.error('Erreur récupération accès client:', error.message);
    }
  };

  const addClientAccess = async () => {
    if (!newEmail.trim()) return;

    try {
      const cree = await rattacherEmail(token, selectedClient.id, newEmail.trim());
      setClientAccess([...clientAccess, cree]);
      setNewEmail('');
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const toggleAccessStatus = async (accessId, currentStatus) => {
    try {
      await changerActivite(token, selectedClient.id, accessId, !currentStatus);
      setClientAccess(clientAccess.map(access =>
        access.id === accessId ? { ...access, isActive: !currentStatus } : access
      ));
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const deleteClientAccess = async (accessId) => {
    if (!window.confirm('Supprimer cet accès email ?')) return;

    try {
      await detacherEmail(token, selectedClient.id, accessId);
      setClientAccess(clientAccess.filter(access => access.id !== accessId));
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const resendInvitation = async (accessId) => {
    setResending(accessId);
    setError('');

    try {
      const reponse = await relancerInvitation(token, selectedClient.id, accessId);
      // Mettre à jour le statut d'invitation dans la liste
      setClientAccess(clientAccess.map(access =>
        access.id === accessId
          ? { ...access, invitation: reponse?.invitation ?? access.invitation }
          : access
      ));
      setError('');
    } catch (error) {
      setError(error.message);
    } finally {
      setResending(null);
    }
  };

  // Helper pour afficher le badge d'invitation
  const getInvitationBadge = (invitation) => {
    if (!invitation) return null;

    const styles = {
      PENDING: { bg: '#fef3c7', color: '#d97706', icon: Clock, text: 'En attente' },
      ACCEPTED: { bg: '#dcfce7', color: '#16a34a', icon: CheckCircle2, text: 'Connecté' },
      EXPIRED: { bg: '#fee2e2', color: '#dc2626', icon: AlertCircle, text: 'Expiré' }
    };

    const cfg = styles[invitation.status] || styles.PENDING;
    const Icon = cfg.icon;

    return (
      <span style={{
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.7rem',
        fontWeight: '500',
        backgroundColor: cfg.bg,
        color: cfg.color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        marginLeft: '8px'
      }}>
        <Icon size={10} />
        {cfg.text}
        {invitation.isGoogleEmail && <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>(Google)</span>}
      </span>
    );
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      email: client.clientEmails?.[0]?.email || ''
    });
    fetchClientAccess(client.id);
    setNewEmail('');
    setShowEditModal(true);
  };

  useEffect(() => {
    let abandonne = false;

    const charger = async () => {
      try {
        const reponse = await fetch(`${config.API_URL}/api/sites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const donnees = await reponse.json();
        if (abandonne) return;

        const index = new Map();
        for (const site of donnees.sites || []) {
          if (!site.clientId) continue;
          if (!index.has(site.clientId)) index.set(site.clientId, []);
          index.get(site.clientId).push(site);
        }
        for (const liste of index.values()) liste.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
        setSitesParClient(index);
      } catch (e) {
        // Une bulle qui ne s'ouvre pas est un moindre mal : l'écran doit rester utilisable.
        console.error('Sites illisibles, la liste ne sera pas affichée au survol :', e.message);
        setSitesParClient(new Map());
      }
    };

    charger();
    return () => { abandonne = true; };
  }, [token]);

  const openAssignModal = (client) => {
    setSelectedClient(client);
    fetchClientSites(client.id);
    setShowAssignModal(true);
  };

  if (loading) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <Loader className="spin" size={24} />
          <p>Chargement des clients...</p>
        </div>
      </div>
    );
  }

  // Une seule liste pour l'affichage et pour le compteur : calculée en deux endroits, elle
  // finirait par afficher un nombre que la liste ne montre pas.
  const recherche = searchQuery.trim().toLowerCase();
  /*
   * Le client choisi dans la barre du haut borne la liste avant la recherche : sur cet
   * écran-ci, le périmètre se réduit à ce seul client. Sans client choisi, rien ne change.
   */
  const clientsDuPerimetre = clientFiltre
    ? clients.filter(client => client.id === clientFiltre)
    : clients;
  const clientsAffiches = [...clientsDuPerimetre]
    .filter(client => !recherche
      || client.name.toLowerCase().includes(recherche)
      || client.clientEmails?.some(e => e.email?.toLowerCase().includes(recherche)))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  /**
   * Le tri de la liste. Le nom par défaut, mais « combien de sites » et « depuis quand » sont
   * des questions qu'on se pose vraiment sur un portefeuille de clients — et auxquelles une
   * grille de cartes ne savait pas répondre.
   */
  const comparer = (a, b) => {
    const sens = tri.sens === 'asc' ? 1 : -1;
    if (tri.cle === 'email') {
      return sens * (a.clientEmails?.[0]?.email || '').localeCompare(b.clientEmails?.[0]?.email || '', 'fr');
    }
    if (tri.cle === 'sites') return sens * ((a._count?.sites || 0) - (b._count?.sites || 0));
    if (tri.cle === 'date') return sens * (new Date(a.createdAt) - new Date(b.createdAt));
    return sens * a.name.localeCompare(b.name, 'fr');
  };

  const clientsTries = [...clientsAffiches].sort(comparer);

  const COLONNES = [
    { cle: 'nom', libelle: 'Client' },
    { cle: 'email', libelle: 'Contact' },
    // La colonne s'étire entre Contact et Créé le : son contenu se centre, sinon le décompte
    // reste collé à gauche et laisse un vide que l'œil lit comme une colonne manquante.
    { cle: 'sites', libelle: 'Sites', centre: true },
    { cle: 'date', libelle: 'Créé le' }
  ];

  return (
    <div className="step-content kc-page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}><Users size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Clients</h2>
        {clients.length > 0 && (
          <span className="kok-compteur">
            {recherche
              ? `${clientsAffiches.length} sur ${clientsDuPerimetre.length}`
              : `${clientsDuPerimetre.length} client${clientsDuPerimetre.length > 1 ? 's' : ''}`}
          </span>
        )}
        {clients.length > 0 && (
          <div className="kc-recherche">
            <Search size={16} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="kc-recherche-effacer" onClick={() => setSearchQuery('')} title="Effacer">
                <X size={14} />
              </button>
            )}
          </div>
        )}
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary btn-small"
          style={{ marginLeft: 'auto', backgroundColor: '#111827', borderColor: '#111827' }}
        >
          + Ajouter un client
        </button>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="kc-liste kcl-liste">
        {clients.length === 0 ? (
          <div className="kc-vide">
            <Users size={40} />
            <p className="kc-vide-titre">Aucun client</p>
            <p className="kc-vide-texte">
              Un client regroupe des sites et ouvre, si vous le souhaitez, un accès en lecture
              seule aux personnes que vous invitez.
            </p>
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary btn-small">
              + Ajouter un client
            </button>
          </div>
        ) : clientsDuPerimetre.length === 0 ? (
          /* « Vide à cause du filtre » n'est pas « aucun résultat de recherche » : sans cette
             branche, l'écran reprocherait une recherche que personne n'a tapée. */
          <div className="kc-vide">
            <Users size={40} />
            <p className="kc-vide-titre">Client introuvable</p>
            <p className="kc-vide-texte">
              Le client choisi dans la barre du haut ne figure pas dans cette liste.
            </p>
          </div>
        ) : clientsTries.length === 0 ? (
          <div className="kc-vide">
            <Search size={40} />
            <p className="kc-vide-titre">Aucun résultat</p>
            <p className="kc-vide-texte">Aucun client ne correspond à « {searchQuery} ».</p>
          </div>
        ) : (
          <>
            <div className="kc-grille kcl-grille kc-entete">
              {COLONNES.map(colonne => (
                <button
                  key={colonne.cle}
                  type="button"
                  className={`kc-tri${tri.cle === colonne.cle ? ' actif' : ''}${colonne.centre ? ' kco-tri-centre' : ''}`}
                  title={`Trier par ${colonne.libelle.toLowerCase()}`}
                  onClick={() => setTri(a => (
                    a.cle === colonne.cle
                      ? { cle: colonne.cle, sens: a.sens === 'asc' ? 'desc' : 'asc' }
                      : { cle: colonne.cle, sens: 'asc' }
                  ))}
                >
                  {colonne.libelle}
                  {tri.cle === colonne.cle
                    ? <ChevronDown size={12} style={{ transform: tri.sens === 'desc' ? 'rotate(180deg)' : 'none' }} />
                    : <ChevronDown size={12} className="kc-tri-repos" />}
                </button>
              ))}
              <span className="kc-entete-fin">Actions</span>
            </div>

            {clientsTries.map(client => {
              const emails = client.clientEmails || [];
              const autres = emails.length - 1;

              return (
                <div key={client.id} className="kc-grille kcl-grille kc-ligne">
                  <div className="kc-marque">
                    <span className="kc-logo"><User size={17} /></span>
                    <span className="kc-nom">{client.name}</span>
                  </div>

                  <div className="kc-compte">
                    {emails[0]?.email || <span className="kli-vide">Aucun e-mail</span>}
                    {/* Les invités supplémentaires sont comptés plutôt qu'énumérés : la colonne
                        ne tiendrait pas, et le détail vit dans la fiche du client. */}
                    {autres > 0 && (
                      <span className="kc-qualificatif">et {autres} autre{autres > 1 ? 's' : ''}</span>
                    )}
                  </div>

                  <span
                    className={`kli-compte${client._count?.sites ? ' ksi-survolable' : ''}`}
                    onMouseEnter={(e) => {
                      if (!client._count?.sites) return;
                      const zone = e.currentTarget.getBoundingClientRect();
                      setSurvol({ clientId: client.id, x: zone.left, bas: zone.bottom + 6, haut: zone.top - 6 });
                    }}
                    onMouseLeave={() => setSurvol(null)}
                  >
                    <Globe size={14} />
                    {client._count?.sites || 0}
                  </span>

                  <span className="kc-date">
                    {new Date(client.createdAt).toLocaleDateString('fr-FR')}
                  </span>

                  <div className="kc-actions">
                    <button
                      type="button"
                      className="kli-action-nommee"
                      onClick={() => openAssignModal(client)}
                      title="Choisir les sites de ce client"
                    >
                      Sites
                    </button>
                    <button type="button" className="kc-action" onClick={() => openEditModal(client)} title="Modifier">
                      <Edit2 size={15} />
                    </button>
                    <button
                      type="button"
                      className="kc-action danger"
                      onClick={() => handleDeleteClient(client.id)}
                      title="Supprimer ce client"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Les sites du client, au survol du décompte. Par portail : `.kc-liste` a
          `overflow: hidden` et rognerait un panneau posé dans la ligne. */}
      {survol && ReactDOM.createPortal(
        <BulleSurvol position={survol}>
          {(() => {
            const liste = sitesParClient?.get(survol.clientId);
            if (sitesParClient === null) return <span className="kbulle-vide">Chargement…</span>;
            if (!liste?.length) return <span className="kbulle-vide">Aucun site retrouvé.</span>;
            return liste.map(site => (
              <div key={site.id} className="kbulle-ligne">
                <span className="kbulle-logo"><Globe size={14} /></span>
                <span className="kbulle-nom">{site.name}</span>
                <span className="kbulle-type">
                  {site.accountIds?.length || 0} compte{(site.accountIds?.length || 0) > 1 ? 's' : ''}
                </span>
              </div>
            ));
          })()}
        </BulleSurvol>,
        document.body
      )}

      {/*
        La même modale que le Gestionnaire, et non une copie.
        Les deux existaient en parallèle et avaient divergé : celle-ci demandait le nom et
        l'adresse de connexion, celle du Gestionnaire le seul nom — un client créé là-bas
        n'avait donc jamais d'accès. Un composant partagé ferme la question.
      */}
      <CreateClientModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        token={token}
        onCreated={() => {
          fetchClients();
          if (onClientChange) onClientChange();
        }}
      />

      {/* Modal Édition */}
      {showEditModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowEditModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier le Client</h2>
              <button 
                onClick={() => setShowEditModal(false)}
                className="btn-icon"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="form-group">
              <label>Nom du client *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            
            {/* Email masqué - géré via la section emails ci-dessous */}
            <input
              type="hidden"
              value={formData.email}
            />

            {/* Section Accès */}
            <div className="access-section">
              <div className="access-header">
                <h3>
                  <Mail size={18} />
                  Emails de connexion ({clientAccess.length})
                </h3>
              </div>

              <div className="access-content">
                  {/* Ajouter un nouvel accès */}
                  <div className="add-access">
                    <div className="add-access-form">
                      <input
                        type="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        placeholder="Ajouter un email..."
                        className="add-access-input"
                      />
                      <button
                        type="button"
                        onClick={addClientAccess}
                        className="btn-primary btn-sm"
                        disabled={!newEmail.trim()}
                      >
                        <Plus size={14} />
                        Ajouter
                      </button>
                    </div>
                  </div>

                  {/* Liste des accès existants */}
                  <div className="access-list">
                    {clientAccess.map(access => (
                      <div key={access.id} className={`access-item ${!access.isActive ? 'inactive' : ''}`}>
                        <div className="access-info">
                          <span className="access-email">
                            {access.email}
                            {access.isPrincipal && <span className="badge-primary">Email de base</span>}
                            {getInvitationBadge(access.invitation)}
                          </span>
                          <span className={`access-status ${access.isActive ? 'active' : 'inactive'}`}>
                            {access.isActive ? 'Actif' : 'Inactif'}
                          </span>
                        </div>

                        <div className="access-actions">
                          {/* Bouton Renvoyer pour PENDING et EXPIRED */}
                          {access.invitation && (access.invitation.status === 'PENDING' || access.invitation.status === 'EXPIRED') && (
                            <button
                              type="button"
                              onClick={() => resendInvitation(access.id)}
                              className="btn-sm btn-info"
                              disabled={resending === access.id}
                              title="Renvoyer l'invitation"
                              style={{
                                backgroundColor: '#e0f2fe',
                                color: '#0284c7',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              {resending === access.id ? (
                                <Loader size={12} className="spin" />
                              ) : (
                                <RefreshCw size={12} />
                              )}
                              Renvoyer
                            </button>
                          )}
                          {!access.isPrincipal && (
                            <>
                              <button
                                type="button"
                                onClick={() => toggleAccessStatus(access.id, access.isActive)}
                                className={`btn-sm ${access.isActive ? 'btn-warning' : 'btn-success'}`}
                                title={access.isActive ? 'Désactiver' : 'Activer'}
                              >
                                {access.isActive ? 'Désactiver' : 'Activer'}
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteClientAccess(access.id)}
                                className="btn-sm btn-danger"
                                title="Supprimer"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
            </div>
            
            <form onSubmit={handleEditClient}>
              <div className="modal-footer">
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  <Save size={16} />
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Assignation Sites */}
      {showAssignModal && selectedClient && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Associer des sites au client {selectedClient.name}</h2>
              <button 
                onClick={() => setShowAssignModal(false)}
                className="btn-icon"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="sites-sections">
              {/* Sites du client */}
              <div className="sites-section">
                <h3>Sites assignés ({sites.length})</h3>
                {sites.length === 0 ? (
                  <p className="empty-text">Aucun site assigné</p>
                ) : (
                  <div className="sites-list">
                    {sites.map(site => (
                      <div key={site.id} className="site-item">
                        <div className="site-info">
                          <h4>{site.name}</h4>
                          <p>{site.description}</p>
                          <small>{site.accountIds.length} comptes associés</small>
                        </div>
                        <button
                          onClick={() => handleAssignSite(site.id, false)}
                          className="btn-danger btn-sm"
                        >
                          Retirer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sites disponibles */}
              <div className="sites-section">
                <h3>Sites disponibles ({availableSites.length})</h3>
                {availableSites.length === 0 ? (
                  <p className="empty-text">Aucun site disponible</p>
                ) : (
                  <div className="sites-list">
                    {availableSites.map(site => (
                      <div key={site.id} className="site-item">
                        <div className="site-info">
                          <h4>{site.name}</h4>
                          <p>{site.description}</p>
                          <small>{site.accountIds.length} comptes associés</small>
                        </div>
                        <button
                          onClick={() => handleAssignSite(site.id, true)}
                          className="btn-primary btn-sm"
                        >
                          Assigner
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => setShowAssignModal(false)}
                className="btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ClientsView;